import React from "react";
import { Eye, Calendar, User, Plus, Users } from "lucide-react";
import { KTProject, KTTask } from "../types/kt-types";
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers.ts";

interface KTProjectsTableProps {
  projects: KTProject[];
  tasks: KTTask[];
  data: any;
  onViewTask: (task: KTTask) => void;
  setShowKTForm: any
  setSelectedProject: any
}

const formatDateUTC = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";

  const day = date.getUTCDate();
  const month = date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  const year = date.getUTCFullYear();

  // Get ordinal suffix for the day
  const getOrdinalSuffix = (n) => {
    if (n > 3 && n < 21) return 'th';
    switch (n % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};


const KTProjectsTable: React.FC<KTProjectsTableProps> = ({
  projects,
  tasks,
  onViewTask,
  data,
  setShowKTForm,
  setSelectedProject,
}) => {
  console.log("KTProjectsTable data", tasks);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-xl font-semibold text-gray-900">My KT Projects</h2>
        <p className="text-gray-600 mt-1">
          View and manage your knowledge transfer projects
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role & Tenure
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Business Unit & Department
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timeline
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Slots</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Manager
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data && data.length > 0 && 
              data.map((project) => {
                const StatusIcon = getStatusIcon(project.KT_Status);
                return (
                  <tr
                    key={project.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {project.RoleForKT}
                        </div>
                        <div className="text-sm text-gray-500">
                          {project.Tenure} Year
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {project.BusinessUnit}
                        </div>
                        <div className="text-sm text-gray-500">
                          {project.Department}
                        </div>
                       
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-900">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <div>
                          <div>{formatDateUTC(project.KtSessionStartDate)}</div>
                          <div className="text-gray-500">
                            to {formatDateUTC(project.KtSessionEndDate)}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div>
                    
                        <div className="text-sm text-gray-500 mt-1">
                          {project.KtSessionTime}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                          project.status
                        )}`}
                      >
                        <StatusIcon className="w-4 h-4" />
                        {project.KT_Status.replace("-", " ").replace(
                          /\b\w/g,
                          (l) => l.toUpperCase()
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-3 h-3 text-gray-600" />
                        </div>
                        <span className="text-sm text-gray-900">
                          {project.manager_email}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap ">
                      <div className="flex gap-2 item-center">
                        <button
                          onClick={() => {
                           
                            setSelectedProject(project);
                            onViewTask(project.RoleForKT);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View Details
                        </button>

                        <button
                          onClick={() => {
                            setSelectedProject(project);
                            setShowKTForm(true);
                          }}
                          disabled={project.kt_progress === "Completed"}
                          className={`inline-flex items-center gap-2 px-3 py-1.5  text-sm font-medium rounded-lg  transition-colors ${project.kt_progress === "Completed" ? "bg-green-200 text-green-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                        >
                          {project.kt_progress === "Completed" ? (
                            <span className="text-gray-500">Completed</span>
                          ) : (
                            <>
                            <Plus className="w-4 h-4" />
                            <span>Add Task</span>
                            </>

                          )}
                          {/* <Plus className="w-4 h-4" />
                         {project.kt_progress === "Completed" ? "Completed" : "Add Task"} */}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            {data && data.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                <div className="text-center py-12">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">
                        No KT found
                      </p>
                      <p className="text-gray-400">
                        Try adjusting your search or filters
                      </p>
                    </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default KTProjectsTable;
